import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';
import { 
    User, Info, Pencil, BookCopy, Route, Camera, Bot, MapPin, X,
    Loader2, CheckCircle, XCircle, Plus, Search, Wallet, Award, 
    Settings, LogOut, ChevronRight, Menu, Sparkles, GraduationCap, Baby, Compass, Map
} from 'lucide-react';
import LoadingAnimation from '../components/LoadingAnimation'; 

// Leaflet ikon düzeltmesi
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_API_URL;

function Profile() {
    const navigate = useNavigate();
    
    // --- State Tanımları ---
    const [profile, setProfile] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const [startedRotas, setStartedRotas] = useState([]);
    
    // --- DİNAMİK STİL STATE'LERİ ---
    const [stylesList, setStylesList] = useState([]); // Veritabanından gelen liste
    const [descriptionStyle, setDescriptionStyle] = useState(null); // Seçili olan
    
    const [yukleniyor, setYukleniyor] = useState(true);
    const [hata, setHata] = useState(null);
    const [isSavingStyle, setIsSavingStyle] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStyleDetail, setSelectedStyleDetail] = useState(null);
    
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // Sayfa yüklendiğinde yeni kullanıcı mı diye kontrol et
    useEffect(() => {
        const isNew = localStorage.getItem('isNewUser');
        if (isNew === 'true') {
            setShowWelcomeModal(true); // Modalı aç
            localStorage.removeItem('isNewUser'); // Bir daha açılmaması için sil
        }
    }, []);
    
    // Token çözümleme
    const getUserIdFromToken = useCallback(() => {
        const token = localStorage.getItem("token");
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            return payload.user_id;
        } catch (e) {
            console.error("Token decode hatası:", e);
            return null;
        }
    }, []);

    const currentUserId = getUserIdFromToken();

    // --- İkon Eşleştirme Yardımcısı ---
    const getIconForStyle = (styleName) => {
        if (!styleName) return <User className="h-5 w-5" />;
        const name = styleName.toLowerCase();
        
        if (name.includes('akademik') || name.includes('tarih')) return <GraduationCap className="h-5 w-5" />;
        if (name.includes('turist') || name.includes('gezi')) return <Route className="h-5 w-5" />;
        if (name.includes('çocuk') || name.includes('basit')) return <Baby className="h-5 w-5" />;
        if (name.includes('edebi') || name.includes('şiir')) return <Pencil className="h-5 w-5" />;
        if (name.includes('efsane') || name.includes('gizem')) return <Sparkles className="h-5 w-5" />;
        
        return <User className="h-5 w-5" />; // Varsayılan
    };

    // Veri Çekme
    const fetchProfileData = useCallback(async () => {
        if (!currentUserId) {
            setHata("Profil bilgilerini görmek için lütfen giriş yapın.");
            setYukleniyor(false);
            navigate("/login");
            return;
        }
        const token = localStorage.getItem("token");
        if (!token) {
            setHata("Giriş token'ı bulunamadı.");
            setYukleniyor(false);
            navigate("/login");
            return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        try {
            setYukleniyor(true);
            setHata(null);

            // Promise.all ile tüm verileri eşzamanlı çekiyoruz
            const [profileRes, predictionsRes, createdRotasRes, startedRotasRes, stylesRes] = await Promise.all([
                axios.get(`${API_URL}/profile`, { headers }),
                axios.get(`${API_URL}/me/predictions`, { headers }),
                axios.get(`${API_URL}/api/users/${currentUserId}/created-rotas`, { headers }),
                axios.get(`${API_URL}/api/users/${currentUserId}/started-rotas`, { headers }),
                axios.get(`${API_URL}/api/styles`, { headers }) 
            ]);

            setProfile(profileRes.data);
            setDescriptionStyle(profileRes.data.description_style || 'genel');
            
            setPredictions(Array.isArray(predictionsRes.data) ? predictionsRes.data : predictionsRes.data.predictions || []);
            setStartedRotas(Array.isArray(startedRotasRes.data) ? startedRotasRes.data : []);
            
            setStylesList(stylesRes.data);

        } catch (err) {
            console.error("Profil verileri alınamadı:", err.response?.data || err.message);
            setHata("Profil verileri yüklenirken bir hata oluştu.");
            if (err.response?.status === 401) {
                navigate("/login");
            }
        } finally {
            setYukleniyor(false);
        }
    }, [currentUserId, navigate]);

    useEffect(() => {
        fetchProfileData();
    }, [fetchProfileData]);

    // Stil Değiştirme
    const handleDescriptionStyleChange = async (newStyle) => {
        if (isSavingStyle || descriptionStyle === newStyle) return;
        
        const previousStyle = descriptionStyle;
        setDescriptionStyle(newStyle);
        setIsSavingStyle(true);
        setSaveMessage(null); 

        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Giriş yapmalısınız.");

            const response = await axios.post(`${API_URL}/description_style`, {
                description_style: newStyle
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.status === 200) {
                setSaveMessage({ type: 'success', text: 'Tercih güncellendi' });
                setProfile(prev => ({ ...prev, description_style: newStyle }));
            }
        } catch (err) {
            setSaveMessage({ type: 'error', text: 'Hata oluştu' });
            setDescriptionStyle(previousStyle);
        } finally {
            setIsSavingStyle(false);
            setTimeout(() => setSaveMessage(null), 3000); 
        }
    };

    // Detay Modalını Açma
    const openDetailModal = (e, styleItem) => {
        e.stopPropagation(); 
        setSelectedStyleDetail(styleItem);
        setIsModalOpen(true);
    };

    // Harita Merkezi Ayarlama
    let centerPosition = [39.0, 35.0];
    let zoom = 6;
    if (predictions?.length > 0) {
        const firstValid = predictions.find(p => p.latitude && p.longitude);
        if (firstValid) {
            centerPosition = [Number(firstValid.latitude), Number(firstValid.longitude)];
            zoom = 10;
        }
    }

    // --- RENDER ---
    
    if (yukleniyor || (profile && descriptionStyle === null)) return <LoadingAnimation />;

    if (hata || !profile) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-red-400 p-6 text-center">
                <XCircle className="h-16 w-16 mb-4 opacity-80" />
                <p className="text-xl mb-6 font-medium">{hata || "Profil bilgisi bulunamadı."}</p>
                <button onClick={() => navigate('/login')} className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-full transition-all shadow-lg hover:shadow-blue-500/30">
                    Giriş Yap
                </button>
            </div>
        );
    }

    return (
        <div className="bg-[#0f172a] min-h-screen font-sans text-slate-100 pb-20 selection:bg-blue-500/30">
            
            {/* === DETAY MODALI === */}
            {isModalOpen && selectedStyleDetail && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm transition-all duration-300">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-5 border-b border-slate-700 bg-slate-900/50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                    {getIconForStyle(selectedStyleDetail.style)}
                                </div>
                                <h3 className="text-lg font-bold text-white">{selectedStyleDetail.style}</h3>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-3">Bu Tarz Nedir?</h4>
                            <p className="text-slate-300 leading-relaxed text-sm">
                                {selectedStyleDetail.explanation}
                            </p>
                        </div>
                        <div className="p-4 bg-slate-900/50 flex justify-end">
                            <button 
                                onClick={() => setIsModalOpen(false)} 
                                className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium"
                            >
                                Anladım
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* === Dekoratif Arka Plan Efektleri === */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 rounded-full blur-[120px]" />
            </div>

            <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
                
                {/* === Hero / Header Bölümü === */}
                <header className="mb-8 md:mb-10 bg-slate-800/40 backdrop-blur-xl border border-white/5 rounded-3xl p-6 md:p-8 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 w-full md:w-auto">
                        <div className="relative">
                            <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg ring-4 ring-slate-800">
                                <User className="h-8 w-8 md:h-10 md:w-10 text-white" />
                            </div>
                            <div className="absolute bottom-1 right-1 bg-green-500 h-4 w-4 md:h-5 md:w-5 rounded-full border-4 border-slate-800"></div>
                        </div>
                        <div className="text-center md:text-left">
                            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">{profile.username}</h1>
                            <p className="text-slate-400 text-sm md:text-base flex items-center justify-center md:justify-start gap-2 break-all">
                                {profile.email}
                            </p>
                            <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-3">
                                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full font-medium border border-blue-500/20">
                                    Kaşif
                                </span>
                                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full font-medium border border-purple-500/20">
                                    {startedRotas.length} Rota
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-8 w-full md:w-auto md:border-l border-white/10 md:pl-8 pt-4 md:pt-0 border-t md:border-t-0 border-white/5 mt-2 md:mt-0">
                         <div className="text-center">
                            {/* --- DİNAMİK CÜZDAN BAKİYESİ BURADA --- */}
                            <p className="text-xl md:text-2xl font-bold text-blue-400">{profile?.tokens || 0}</p>
                            <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Kredi (Token)</p>
                         </div>
                         <div className="text-center">
                            <p className="text-xl md:text-2xl font-bold text-purple-400">{predictions.length}</p>
                            <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Tahmin</p>
                         </div>
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
                    
                    {/* === Sol Sütun: Ayarlar ve Durum === */}
                    <div className="lg:col-span-4 space-y-6 order-2 lg:order-1">
                        
                        {/* --- DİNAMİK ANLATIM TARZI SEÇİMİ --- */}
                        <section className="bg-slate-800/50 backdrop-blur-md p-5 md:p-6 rounded-3xl border border-white/5 shadow-xl">
                            <div className="flex items-center gap-3 mb-4 md:mb-6">
                                <div className="p-2 bg-blue-500/20 rounded-lg">
                                    <Pencil className="h-5 w-5 text-blue-400" />
                                </div>
                                <h2 className="text-lg md:text-xl font-bold text-white">Yapay Zeka Anlatımı</h2>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                {stylesList.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => handleDescriptionStyleChange(item.style)}
                                        disabled={isSavingStyle}
                                        className={`relative flex flex-col items-center justify-center p-3 md:p-4 rounded-2xl transition-all duration-300 border group
                                            ${descriptionStyle === item.style
                                                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/20 scale-[1.02]'
                                                : 'bg-slate-700/30 border-slate-600/30 text-slate-400 hover:bg-slate-700/60 hover:border-slate-500'
                                            }`}
                                    >
                                        <div 
                                            onClick={(e) => openDetailModal(e, item)}
                                            className={`absolute top-2 right-2 p-1.5 rounded-full transition-colors cursor-pointer z-10
                                                ${descriptionStyle === item.style ? 'text-white/70 hover:bg-white/20' : 'text-slate-500 hover:bg-slate-600 hover:text-white'}`}
                                            title="Detaylı Bilgi"
                                        >
                                            <Info size={14} />
                                        </div>

                                        <div className="mb-2 opacity-80 transition-transform group-hover:scale-110 duration-200">
                                            {getIconForStyle(item.style)}
                                        </div>
                                        <span className="font-semibold text-xs md:text-sm">{item.style}</span>
                                    </button>
                                ))}
                            </div>
                            
                            <div className="h-6 mt-4 flex items-center justify-center text-sm">
                                {isSavingStyle && <span className="flex items-center text-blue-400 gap-2"><Loader2 className="h-4 w-4 animate-spin"/> Kaydediliyor...</span>}
                                {saveMessage && <span className={saveMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}>{saveMessage.text}</span>}
                            </div>
                        </section>

                        {/* Kredi & Rozet Kartları */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                             {/* Kredi Kartı */}
                            <div className="bg-gradient-to-r from-slate-800 to-slate-800/60 p-5 md:p-6 rounded-3xl border border-white/5 shadow-lg relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Wallet className="h-20 w-20 md:h-24 md:w-24 -rotate-12" />
                                </div>
                                <h3 className="text-slate-400 text-xs md:text-sm font-medium mb-1">Mevcut Bakiyen</h3>
                                {/* --- DİNAMİK CÜZDAN BAKİYESİ BURADA DA GÜNCELLENDİ --- */}
                                <div className="text-2xl md:text-3xl font-bold text-white mb-4">{profile?.tokens || 0} <span className="text-sm font-normal text-slate-500">Token</span></div>
                                <button onClick={() => navigate('/pricing')} className="text-xs md:text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
                                    Token Satın Al <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>

                             {/* Rozet Kartı */}
                             <div className="bg-gradient-to-r from-slate-800 to-slate-800/60 p-5 md:p-6 rounded-3xl border border-white/5 shadow-lg relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Award className="h-20 w-20 md:h-24 md:w-24 rotate-12" />
                                </div>
                                <h3 className="text-slate-400 text-xs md:text-sm font-medium mb-1">Rozet Durumu</h3>
                                <div className="text-white mb-4 text-xs md:text-sm pr-8">Yeni rozetler kazanmak için keşfetmeye devam et!</div>
                                <button onClick={() => navigate('/badge-system')} className="text-xs md:text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors">
                                    Rozet Sistemi <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* === Sağ Sütun: İçerik === */}
                    <div className="lg:col-span-8 space-y-6 md:space-y-8 order-1 lg:order-2">
                        
                        {/* Başlatılan Rotalar */}
                        <section>
                            <div className="flex items-center justify-between mb-4 px-2">
                                <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                                    <Route className="h-5 w-5 text-green-400" /> Devam Eden Rotalar
                                </h2>
                            </div>
                            
                            {startedRotas.length === 0 ? (
                                <div className="bg-slate-800/30 border border-dashed border-slate-700 rounded-3xl p-6 md:p-8 text-center">
                                    <p className="text-slate-400 mb-4 text-sm md:text-base">Henüz aktif bir rotan yok.</p>
                                    <button onClick={() => navigate('/route')} className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors">
                                        Rotaları Keşfet
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                                    {startedRotas.map((item) => (
                                        <div 
                                            key={item.id} 
                                            onClick={() => navigate(`/rotalar/${item.rota_id}`)}
                                            className="group relative bg-slate-800 rounded-2xl overflow-hidden cursor-pointer border border-white/5 hover:border-blue-500/50 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                                        >
                                            <div className="h-36 md:h-40 relative">
                                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent z-10" />
                                                <img 
                                                    src={item.rota_obj?.gorsel || 'https://via.placeholder.com/400x200?text=Rota'} 
                                                    alt="Rota" 
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                />
                                                <div className="absolute bottom-3 left-4 z-20">
                                                    <h3 className="text-base md:text-lg font-bold text-white">{item.rota_obj?.baslik || 'Yükleniyor...'}</h3>
                                                    <p className="text-[10px] md:text-xs text-slate-300">{item.rota_obj?.il}, {item.rota_obj?.ilce}</p>
                                                </div>
                                            </div>
                                            <div className="p-3 md:p-4 flex justify-between items-center bg-slate-800">
                                                <div className="text-[10px] md:text-xs text-slate-400">
                                                   {item.is_completed ? "Tamamlandı" : `${item.completed_mekan_indices?.length || 0} / ${item.rota_obj?.mekanDetaylari_iliskisi?.length || 0} Durak`}
                                                </div>
                                                {item.is_completed 
                                                    ? <CheckCircle className="h-5 w-5 text-green-500" />
                                                    : <div className="h-2 w-16 md:w-20 bg-slate-700 rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-blue-500" 
                                                            style={{width: `${((item.completed_mekan_indices?.length || 0) / (item.rota_obj?.mekanDetaylari_iliskisi?.length || 1)) * 100}%`}}
                                                        />
                                                      </div>
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Tahmin Geçmişi ve Harita Tab'i */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 md:gap-8">
                            
                            {/* Harita */}
                            <section className="bg-slate-800/50 p-1 rounded-3xl border border-white/10 shadow-xl h-64 md:h-[400px]">
                                <div className="h-full w-full rounded-[20px] overflow-hidden relative z-0">
                                    {predictions.length > 0 ? (
                                        <MapContainer center={centerPosition} zoom={zoom} scrollWheelZoom={true} className="h-full w-full">
                                            <TileLayer
                                                attribution='&copy; OpenStreetMap'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {predictions.map((pred, i) => (
                                                pred.latitude && pred.longitude && (
                                                    <Marker key={i} position={[Number(pred.latitude), Number(pred.longitude)]}>
                                                        <Popup>
                                                            <div className="text-slate-900 font-semibold">{pred.location}</div>
                                                        </Popup>
                                                    </Marker>
                                                )
                                            ))}
                                        </MapContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center bg-slate-800 text-slate-500 text-sm">
                                            Harita verisi yok.
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Son Tahminler Listesi */}
                            <section className="flex flex-col h-auto md:h-[400px]">
                                <div className="flex items-center justify-between mb-4">
                                     <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                                        <Camera className="h-5 w-5 text-blue-400" /> Son Keşiflerin
                                     </h2>
                                     <button onClick={() => navigate('/upload')} className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded-full transition-colors">
                                        + Yeni
                                     </button>
                                </div>
                                
                                <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar max-h-80 md:max-h-none">
                                    {predictions.length === 0 ? (
                                        <div className="text-center py-10 text-slate-500 text-sm">Henüz bir fotoğraf yüklemedin.</div>
                                    ) : (
                                            predictions.map((pred, i) => (
                                            <div key={i} className="flex gap-3 bg-slate-800 p-3 rounded-xl border border-white/5 hover:bg-slate-750 transition-colors">
                                                <img src={`${API_URL}${pred.image_url}`} alt="Thumb" className="h-14 w-14 md:h-16 md:w-16 rounded-lg object-cover bg-slate-900 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-white font-medium text-sm truncate">{pred.location || 'Konum Belirlenemedi'}</h4>
                                                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{pred.note || 'Not yok.'}</p>
                                                    <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500">
                                                        <span>%{Math.round(pred.confidence * 100)} Güven</span>
                                                        <span>•</span>
                                                        <span>{new Date(pred.timestamp).toLocaleDateString('tr-TR')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>
            {showWelcomeModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                {/* Arka plan bulanıklığı */}
                <div 
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    onClick={() => setShowWelcomeModal(false)}
                ></div>
                
                {/* Modal İçeriği */}
                <div className="relative bg-slate-900 border border-slate-700/50 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-500">
                    {/* Üst Dekoratif Alan */}
                    <div className="h-32 bg-gradient-to-br from-blue-600 to-indigo-800 relative overflow-hidden flex items-center justify-center">
                        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
                        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="absolute -top-10 -left-10 w-32 h-32 bg-black/10 rounded-full blur-2xl"></div>
                        
                        {/* İkon */}
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-2xl flex items-center justify-center z-10 rotate-3 transform hover:rotate-6 transition-transform">
                            <Compass size={40} className="text-blue-600" />
                        </div>
                    </div>

                    {/* Metin Alanı */}
                    <div className="p-8 text-center relative z-10">
                        <h2 className="text-3xl font-black text-white mb-2 tracking-tight">Mekânname'ye<br/>Hoş Geldin! 🌍</h2>
                        <p className="text-slate-400 mb-6 leading-relaxed text-sm md:text-base">
                            Seni aramızda görmek harika! Hesabın başarıyla oluşturuldu ve cüzdanına <span className="text-blue-400 font-bold">300 Token</span> hoş geldin hediyesi yüklendi.
                        </p>
                        
                        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 text-left mb-8 space-y-3">
                            <div className="flex items-start gap-3">
                                <Camera className="text-blue-400 shrink-0 mt-0.5" size={18} />
                                <p className="text-xs md:text-sm text-slate-300"><strong className="text-white">Fotoğraf Analizi:</strong> Bilmediğin tarihi mekânların fotoğrafını çek, AI senin için anında tanısın.</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <Map className="text-indigo-400 shrink-0 mt-0.5" size={18} />
                                <p className="text-xs md:text-sm text-slate-300"><strong className="text-white">Akıllı Rotalar:</strong> Bütçene ve zamanına göre kusursuz seyahat planları oluştur.</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <Bot className="text-purple-400 shrink-0 mt-0.5" size={18} />
                                <p className="text-xs md:text-sm text-slate-300"><strong className="text-white">Sesli Hikayeler:</strong> Mekânların gizemli tarihlerini yapay zeka sesinden dinle.</p>
                            </div>
                        </div>

                        {/* Aksiyon Butonları */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button 
                                onClick={() => {
                                    setShowWelcomeModal(false);
                                    navigate('/help'); // Veya '/about', 'nasil-calisir' sayfanın rotası neyse
                                }}
                                className="flex-1 py-3 px-4 rounded-xl font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 transition-all border border-slate-700"
                            >
                                Nasıl Çalışır?
                            </button>
                            <button 
                                onClick={() => setShowWelcomeModal(false)}
                                className="flex-1 py-3 px-4 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/50 transition-all active:scale-95 flex items-center justify-center gap-2"
                            >
                                Hemen Başla <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                    
                    {/* Sağ Üst Kapatma Çarpısı */}
                    <button 
                        onClick={() => setShowWelcomeModal(false)} 
                        className="absolute top-4 right-4 text-white/50 hover:text-white bg-black/20 hover:bg-black/40 rounded-full p-1.5 backdrop-blur-sm transition-all"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>
        )}
        </div>
    );
}

export default Profile;